import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { fetchSettings, logoSrc } from "@/lib/settings";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import {
  Target, NotebookPen, MessageSquareReply, GraduationCap,
  MapPin, Phone, Menu, X, Apple, Play, ArrowRight, ArrowUp,
  Sparkles, Users, CheckCircle2, Shield, Heart, TrendingUp, ClipboardList,
} from "lucide-react";

const T = {
  tr: {
    nav_home: "Anasayfa", nav_about: "Hakkımızda", nav_services: "Hizmetler", nav_contact: "İletişim",
    login: "Giriş Yap",
    hero_badge: "Renka Özel Eğitim ve Gelişim Merkezi",
    hero_title_1: "Özel Eğitimde",
    hero_title_2: "Dijital Dönüşüm",
    hero_sub: "Renka; öğrenci gelişimini, bireyselleştirilmiş eğitim planlarını (BEP) ve aile iş birliğini tek bir modern platformda buluşturur.",
    hero_cta: "Veli Girişi",
    hero_cta2: "Hizmetlerimiz",
    about_title: "Hakkımızda",
    about_text: "Renka Özel Eğitim ve Gelişim Merkezi, her çocuğun kendine özgü potansiyelini ortaya çıkarmak için uzman kadrosu ve teknoloji odaklı yaklaşımıyla hizmet verir. Öğretmen, veli ve uzmanları aynı ekranda buluşturarak şeffaf ve ölçülebilir bir gelişim süreci sunarız.",
    services_title: "Hizmetlerimiz",
    services_sub: "Çocuğunuzun gelişimini destekleyen bütüncül bir yaklaşım.",
    s1_t: "Bireysel Eğitim Planı", s1_d: "Gelişim alanlarına göre hedefler ve ölçülebilir ilerleme takibi.",
    s2_t: "Günlük Gelişim Notları", s2_d: "Öğretmenlerden tarih bazlı, şeffaf günlük gözlem notları.",
    s3_t: "Aile Görevleri", s3_d: "Evde uygulanabilir haftalık görevler ve foto/video geri bildirim.",
    s4_t: "Uzman Kadro", s4_d: "Alanında deneyimli özel eğitim öğretmenleri ve uzmanlar.",
    process_title: "Nasıl Çalışır?",
    process_sub: "Öğretmen, veli ve uzman aynı dijital platformda buluşur.",
    step1_t: "BEP Planı Oluşturulur", step1_d: "Öğrenciye özel hedefler ve gelişim alanları belirlenir.",
    step2_t: "Veli Takip Eder", step2_d: "Ev görevleri, notlar ve geri bildirimler anlık paylaşılır.",
    step3_t: "Gelişim Raporlanır", step3_d: "İlerleme grafiklerle izlenir, aile bilgilendirilir.",
    cta_title: "Çocuğunuzun gelişim yolculuğuna bugün başlayın",
    cta_sub: "Veli portalına giriş yaparak BEP planını, görevleri ve günlük notları takip edin.",
    contact_title: "İletişim",
    contact_sub: "Bize ulaşın, birlikte planlayalım.",
    contact_addr_label: "Adres",
    contact_addr: "Girne, Kuzey Kıbrıs (KKTC)",
    contact_phone_label: "Telefon",
    footer_tagline: "Özel eğitimde dijital dönüşüm.",
    download: "Uygulamayı indirin",
    rights: "Tüm hakları saklıdır.",
    designed: "Designed by",
    trust1: "Güvenli Platform", trust2: "Uzman Kadro", trust3: "Aile İş Birliği",
  },
  en: {
    nav_home: "Home", nav_about: "About", nav_services: "Services", nav_contact: "Contact",
    login: "Sign In",
    hero_badge: "Renka Special Education & Development Center",
    hero_title_1: "Digital Transformation",
    hero_title_2: "in Special Education",
    hero_sub: "Renka brings student progress, individualized education plans (IEP) and family collaboration together in one modern platform.",
    hero_cta: "Parent Login",
    hero_cta2: "Our Services",
    about_title: "About Us",
    about_text: "Renka Special Education & Development Center helps every child reveal their unique potential with an expert team and a technology-driven approach. We unite teachers, parents and specialists on one screen for a transparent, measurable development journey.",
    services_title: "Our Services",
    services_sub: "A holistic approach that supports your child's development.",
    s1_t: "Individual Education Plan", s1_d: "Goals by development area with measurable progress tracking.",
    s2_t: "Daily Progress Notes", s2_d: "Transparent, date-based daily observation notes from teachers.",
    s3_t: "Family Tasks", s3_d: "Weekly at-home tasks with photo/video feedback.",
    s4_t: "Expert Team", s4_d: "Experienced special-education teachers and specialists.",
    process_title: "How It Works",
    process_sub: "Teachers, parents and specialists meet on one digital platform.",
    step1_t: "IEP Plan Created", step1_d: "Personalized goals and development areas are defined.",
    step2_t: "Parent Follows", step2_d: "Home tasks, notes and feedback are shared instantly.",
    step3_t: "Progress Reported", step3_d: "Progress is tracked with charts, family stays informed.",
    cta_title: "Start your child's development journey today",
    cta_sub: "Sign in to the parent portal to follow IEP plans, tasks and daily notes.",
    contact_title: "Contact",
    contact_sub: "Reach out and let's plan together.",
    contact_addr_label: "Address",
    contact_addr: "Kyrenia, Northern Cyprus (TRNC)",
    contact_phone_label: "Phone",
    footer_tagline: "Digital transformation in special education.",
    download: "Download the app",
    rights: "All rights reserved.",
    designed: "Designed by",
    trust1: "Secure Platform", trust2: "Expert Team", trust3: "Family Collaboration",
  },
};

const PHONE = "+90 548 861 00 00";
const PHONE_HREF = "tel:+905488610000";
const SERVICE_ICONS = [Target, NotebookPen, MessageSquareReply, GraduationCap, Sparkles, Heart, Shield, TrendingUp];
const SERVICE_COLORS = [
  "from-violet-500 to-violet-600", "from-blue-500 to-blue-600",
  "from-fuchsia-500 to-pink-600", "from-emerald-500 to-teal-600",
  "from-amber-500 to-orange-600", "from-rose-500 to-red-600",
  "from-indigo-500 to-indigo-600", "from-cyan-500 to-cyan-600",
];
const PREVIEW_COLORS = ["text-violet-600 bg-violet-50", "text-blue-600 bg-blue-50", "text-emerald-600 bg-emerald-50"];
const PROCESS_ICONS = [Target, Users, TrendingUp];
const TRUST_ICONS = [Shield, GraduationCap, Heart];

function cmsVal(cms, key, fallback) {
  const v = cms?.[key];
  return v != null && v !== "" ? v : fallback;
}

function PlatformPreview({ logoUrl, portalLabel, previewStats, progressLabel, progressValue, tasks }) {
  return (
    <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
      <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-violet-400/20 blur-2xl" />
      <div className="absolute -bottom-6 -left-6 h-32 w-32 rounded-full bg-blue-400/20 blur-2xl" />
      <div className="relative overflow-hidden rounded-3xl border border-white/60 bg-white/80 p-4 shadow-[0_24px_80px_rgba(80,40,180,0.18)] backdrop-blur-sm sm:p-5">
        <div className="mb-4 flex items-center justify-between rounded-2xl bg-slate-900 px-4 py-3">
          <img src={logoUrl} alt="Renka" className="h-7 w-auto object-contain brightness-0 invert" onError={(e) => { e.currentTarget.src = "/renka-logo.png"; }} />
          <span className="rounded-full bg-violet-500/20 px-2.5 py-0.5 text-[10px] font-semibold text-violet-300">{portalLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {previewStats.map(({ value, label }, i) => (
            <div key={`${label}-${i}`} className={`rounded-xl p-3 text-center ${PREVIEW_COLORS[i % PREVIEW_COLORS.length]}`}>
              <p className="font-heading text-lg font-bold sm:text-xl">{value}</p>
              <p className="mt-0.5 text-[10px] font-medium opacity-80 sm:text-xs">{label}</p>
            </div>
          ))}
        </div>
        <div className="mt-3 space-y-2">
          <div className="rounded-xl border border-slate-100 bg-slate-50/80 p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold text-slate-700">{progressLabel}</span>
              <span className="text-xs font-bold text-violet-600">{progressValue}</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-slate-200">
              <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-500" style={{ width: progressValue?.includes("%") ? progressValue : "72%" }} />
            </div>
          </div>
          {tasks.map((task) => (
            <div key={task} className="flex items-center gap-2.5 rounded-xl border border-slate-100 bg-white px-3 py-2.5">
              <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-violet-100">
                <ClipboardList className="h-3.5 w-3.5 text-violet-600" />
              </div>
              <span className="truncate text-xs font-medium text-slate-700">{task}</span>
              <CheckCircle2 className="ml-auto h-4 w-4 flex-shrink-0 text-emerald-500" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem("renka_lang") || "tr");
  const [menuOpen, setMenuOpen] = useState(false);
  const [cms, setCms] = useState(null);
  const [navPages, setNavPages] = useState([]);
  const t = T[lang];

  useEffect(() => {
    fetchSettings().then(setCms).catch(() => {});
    api.get("/site/pages/navigation").then((r) => setNavPages(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    document.title = cms?.seo_title || "Renka Özel Eğitim ve Gelişim Merkezi - Kıbrıs";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", cms?.seo_description || "Renka Özel Eğitim ve Gelişim Merkezi — Kıbrıs, Girne'de özel eğitim, bireyselleştirilmiş eğitim planı (BEP), gelişim takibi ve aile iş birliği.");
  }, [cms]);

  const useCms = lang === "tr" && cms;
  const c = (key, fallback) => (useCms ? cmsVal(cms, key, fallback) : fallback);

  const navHome = c("nav_home", t.nav_home);
  const navAbout = c("nav_about", t.nav_about);
  const navServices = c("nav_services", t.nav_services);
  const navContact = c("nav_contact", t.nav_contact);
  const loginLabel = c("login_label", t.login);
  const heroBadge = c("hero_badge", t.hero_badge);
  const heroTitle1 = c("hero_title_1", t.hero_title_1);
  const heroTitle2 = c("hero_title_2", t.hero_title_2);
  const heroSub = c("hero_sub", t.hero_sub);
  const heroCta = c("hero_cta", t.hero_cta);
  const heroCta2 = c("hero_cta2", t.hero_cta2);
  const aboutTitle = c("about_title", t.about_title);
  const aboutText = c("about_text", t.about_text);
  const servicesTitle = c("services_title", t.services_title);
  const servicesSub = c("services_sub", t.services_sub);
  const processTitle = c("process_title", t.process_title);
  const processSub = c("process_sub", t.process_sub);
  const ctaTitle = c("cta_title", t.cta_title);
  const ctaSub = c("cta_sub", t.cta_sub);
  const ctaBtnContact = c("cta_btn_contact", t.contact_title);
  const contactTitle = c("contact_title", t.contact_title);
  const contactSub = c("contact_sub", t.contact_sub);
  const contactAddrLabel = c("contact_addr_label", t.contact_addr_label);
  const contactPhoneLabel = c("contact_phone_label", t.contact_phone_label);
  const contactCallout = c("contact_callout", lang === "tr" ? "Randevu ve bilgi için bizi arayın" : "Call us for appointments and info");
  const footerTagline = c("footer_tagline", t.footer_tagline);
  const footerLinksTitle = c("footer_links_title", lang === "tr" ? "Hızlı Bağlantılar" : "Quick Links");
  const footerDownloadLabel = c("footer_download_label", t.download);
  const footerRights = c("footer_rights", t.rights);
  const previewPortalLabel = c("preview_portal_label", "Veli Portalı");
  const previewProgressLabel = c("preview_progress_label", "BEP İlerlemesi");
  const previewProgressValue = c("preview_progress_value", "72%");

  const phone = cms?.phone || PHONE;
  const phoneHref = cms?.phone_href || PHONE_HREF;
  const address = c("address", t.contact_addr);
  const iosUrl = cms?.ios_url || "#";
  const androidUrl = cms?.android_url || "#";
  const logoUrl = logoSrc(cms);

  const changeLang = (l) => { setLang(l); localStorage.setItem("renka_lang", l); };
  const goLogin = () => navigate("/login");
  const scrollTo = (id) => { setMenuOpen(false); document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); };
  const scrollTop = () => { setMenuOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const [showTop, setShowTop] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LangSwitch = () => (
    <div className="flex items-center rounded-full border border-slate-200 bg-white p-0.5" data-testid="lang-switch">
      <button onClick={() => changeLang("tr")} data-testid="lang-tr" className={`rounded-full px-2.5 py-1 text-sm font-medium transition-colors ${lang === "tr" ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>🇹🇷 TR</button>
      <button onClick={() => changeLang("en")} data-testid="lang-en" className={`rounded-full px-2.5 py-1 text-sm font-medium transition-colors ${lang === "en" ? "bg-violet-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>🇬🇧 EN</button>
    </div>
  );

  const services = (useCms && cms.services?.length ? cms.services : [
    { title: t.s1_t, description: t.s1_d }, { title: t.s2_t, description: t.s2_d },
    { title: t.s3_t, description: t.s3_d }, { title: t.s4_t, description: t.s4_d },
  ]).map((sv, i) => ({ icon: SERVICE_ICONS[i % SERVICE_ICONS.length], title: sv.title, d: sv.description, c: SERVICE_COLORS[i % SERVICE_COLORS.length] }));

  const stats = (useCms && cms.stats?.length ? cms.stats.map((x) => [x.value, x.label]) : [
    ["10+", lang === "tr" ? "Yıllık Deneyim" : "Years Experience"],
    ["100%", lang === "tr" ? "Bireysel Plan" : "Individual Plans"],
    ["7/24", lang === "tr" ? "Dijital Takip" : "Digital Tracking"],
    ["1:1", lang === "tr" ? "Uzman Desteği" : "Expert Support"],
  ]);

  const trustBadges = (useCms && cms.trust_badges?.length ? cms.trust_badges : [
    { label: t.trust1 }, { label: t.trust2 }, { label: t.trust3 },
  ]).map((tb, i) => ({ icon: TRUST_ICONS[i % TRUST_ICONS.length], label: tb.label }));

  const previewStats = (useCms && cms.preview_stats?.length ? cms.preview_stats : [
    { value: "12", label: "BEP Hedefi" }, { value: "5", label: "Bu Hafta" }, { value: "98%", label: "Tamamlama" },
  ]);

  const previewTasks = (useCms && cms.preview_tasks?.length ? cms.preview_tasks.map((x) => x.title) : [
    "Duyguları ifade etme", "10 dk odaklanma çalışması",
  ]);

  const processSteps = (useCms && cms.process_steps?.length ? cms.process_steps : [
    { title: t.step1_t, description: t.step1_d },
    { title: t.step2_t, description: t.step2_d },
    { title: t.step3_t, description: t.step3_d },
  ]).map((step, i) => ({ icon: PROCESS_ICONS[i % PROCESS_ICONS.length], title: step.title, d: step.description }));

  const headerPages = navPages.filter((p) => p.show_in_header);
  const footerPages = navPages.filter((p) => p.show_in_footer);
  const anchorNav = [
    [scrollTop, navHome],
    [() => scrollTo("about"), navAbout],
    [() => scrollTo("services"), navServices],
    [() => scrollTo("contact"), navContact],
  ];

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-100/80 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <Logo className="h-8 w-auto object-contain sm:h-9" alt="RENKA" />
          <nav className="hidden items-center gap-8 md:flex">
            {anchorNav.map(([fn, label]) => (
              <button key={label} onClick={fn} className="text-sm font-medium text-slate-600 transition-colors hover:text-violet-600">{label}</button>
            ))}
            {headerPages.map((p) => (
              <Link key={p.id} to={`/sayfa/${p.slug}`} className="text-sm font-medium text-slate-600 transition-colors hover:text-violet-600">{p.title}</Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <LangSwitch />
            <Button onClick={goLogin} data-testid="header-login-btn" className="h-9 rounded-full bg-violet-600 px-4 text-sm font-semibold text-white hover:bg-violet-700">{loginLabel}</Button>
            <button onClick={() => setMenuOpen(!menuOpen)} className="p-1 text-slate-600 md:hidden" aria-label="Menu">{menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}</button>
          </div>
        </div>
        {menuOpen && (
          <div className="border-t border-slate-100 bg-white px-4 py-3 md:hidden">
            <button onClick={scrollTop} className="block w-full py-2 text-left text-sm font-medium text-slate-600">{navHome}</button>
            <button onClick={() => scrollTo("about")} className="block w-full py-2 text-left text-sm font-medium text-slate-600">{navAbout}</button>
            <button onClick={() => scrollTo("services")} className="block w-full py-2 text-left text-sm font-medium text-slate-600">{navServices}</button>
            <button onClick={() => scrollTo("contact")} className="block w-full py-2 text-left text-sm font-medium text-slate-600">{navContact}</button>
            {headerPages.map((p) => (
              <Link key={p.id} to={`/sayfa/${p.slug}`} onClick={() => setMenuOpen(false)} className="block w-full py-2 text-left text-sm font-medium text-slate-600">{p.title}</Link>
            ))}
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-violet-50/80 via-white to-white">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -right-20 top-0 h-[28rem] w-[28rem] rounded-full bg-violet-200/50 blur-[100px]" />
          <div className="absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-blue-200/40 blur-[100px]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:32px_32px]" />
        </div>
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 py-16 sm:px-6 sm:py-20 lg:grid-cols-2 lg:gap-16 lg:px-8 lg:py-28">
          <div className="text-center lg:text-left">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white px-4 py-1.5 text-xs font-semibold text-violet-700 shadow-sm">
              <Sparkles className="h-3.5 w-3.5" /> {heroBadge}
            </span>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-[3.25rem]">
              {heroTitle1}{" "}
              <span className="bg-gradient-to-r from-violet-600 via-blue-600 to-fuchsia-600 bg-clip-text text-transparent">{heroTitle2}</span>
            </h1>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg lg:max-w-xl">{heroSub}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3 lg:justify-start">
              <Button onClick={goLogin} data-testid="hero-veli-giris" className="h-12 rounded-full bg-violet-600 px-7 text-base font-semibold text-white shadow-lg shadow-violet-600/25 hover:bg-violet-700 active:scale-[0.98]">
                {heroCta} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button onClick={() => scrollTo("services")} variant="outline" className="h-12 rounded-full border-slate-200 bg-white px-7 text-base font-semibold hover:bg-slate-50">{heroCta2}</Button>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4 lg:justify-start">
              {trustBadges.map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500">
                  <Icon className="h-4 w-4 text-violet-500" /> {label}
                </span>
              ))}
            </div>
          </div>
          <PlatformPreview
            logoUrl={logoUrl}
            portalLabel={previewPortalLabel}
            previewStats={previewStats}
            progressLabel={previewProgressLabel}
            progressValue={previewProgressValue}
            tasks={previewTasks}
          />
        </div>
      </section>

      {/* Stats band */}
      <section className="border-y border-violet-100 bg-gradient-to-r from-violet-600 via-violet-700 to-blue-700 py-10 sm:py-12">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 sm:grid-cols-4 sm:gap-8 sm:px-6 lg:px-8">
          {stats.map(([n, l]) => (
            <div key={l} className="text-center">
              <p className="font-heading text-3xl font-extrabold text-white sm:text-4xl">{n}</p>
              <p className="mt-1 text-sm font-medium text-violet-200">{l}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About */}
      <section id="about" className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-violet-100 to-blue-100 opacity-60 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-slate-100 bg-gradient-to-br from-violet-50 via-white to-blue-50 p-8 shadow-[0_20px_60px_rgba(80,40,180,0.08)] sm:p-10">
              <img src={logoUrl} alt="Renka" className="mx-auto h-20 w-auto object-contain sm:h-24" onError={(e) => { e.currentTarget.src = "/renka-logo.png"; }} />
              <div className="mt-8 grid grid-cols-2 gap-3">
                {stats.slice(0, 4).map(([n, l]) => (
                  <div key={l} className="rounded-2xl border border-white/80 bg-white/70 p-4 text-center backdrop-blur-sm">
                    <p className="font-heading text-2xl font-bold text-violet-600">{n}</p>
                    <p className="mt-0.5 text-xs text-slate-500">{l}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-violet-600">{aboutTitle}</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl lg:text-[2.75rem]">{aboutTitle}</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">{aboutText}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {services.slice(0, 4).map(({ icon: Icon, title }) => (
                <span key={title} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-xs font-semibold text-slate-700">
                  <Icon className="h-3.5 w-3.5 text-violet-600" /> {title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="bg-slate-50 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-violet-600">{servicesTitle}</span>
            <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">{servicesTitle}</h2>
            <p className="mt-4 text-base text-slate-600 sm:text-lg">{servicesSub}</p>
          </div>
          <div className={`mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 ${services.length >= 3 ? "lg:grid-cols-3" : ""} ${services.length === 4 ? "lg:grid-cols-2 xl:grid-cols-4" : ""}`}>
            {services.map(({ icon: Icon, title, d, c }, i) => (
              <div key={title} className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(80,40,180,0.10)] sm:p-7">
                <span className="absolute right-4 top-4 font-heading text-4xl font-extrabold text-slate-100 transition-colors group-hover:text-violet-100">{String(i + 1).padStart(2, "0")}</span>
                <div className={`relative mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${c} shadow-lg`}>
                  <Icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="relative font-heading text-lg font-bold text-slate-900 sm:text-xl">{title}</h3>
                <p className="relative mt-2.5 text-sm leading-relaxed text-slate-500 sm:text-[15px]">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-violet-600">{processTitle}</span>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">{processTitle}</h2>
          <p className="mt-4 text-base text-slate-600">{processSub}</p>
        </div>
        <div className="relative mt-14 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6">
          <div className="absolute left-[16.67%] right-[16.67%] top-10 hidden h-0.5 bg-gradient-to-r from-violet-200 via-blue-200 to-violet-200 md:block" />
          {processSteps.map(({ icon: Icon, title, d }, i) => (
            <div key={title} className="relative text-center">
              <div className="relative mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-2xl border-4 border-white bg-gradient-to-br from-violet-500 to-blue-600 shadow-xl shadow-violet-500/25">
                <Icon className="h-9 w-9 text-white" />
                <span className="absolute -right-2 -top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-bold text-violet-600 shadow-md">{i + 1}</span>
              </div>
              <h3 className="font-heading text-lg font-bold text-slate-900">{title}</h3>
              <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-slate-500">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-4 mb-20 sm:mx-6 lg:mx-8">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-violet-700 to-blue-700 px-6 py-14 text-center sm:px-12 sm:py-16">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff0a_1px,transparent_1px),linear-gradient(to_bottom,#ffffff0a_1px,transparent_1px)] bg-[size:24px_24px]" />
          <div className="relative">
            <h2 className="mx-auto max-w-2xl font-heading text-2xl font-bold text-white sm:text-3xl lg:text-4xl">{ctaTitle}</h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-violet-100">{ctaSub}</p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button onClick={goLogin} className="h-12 rounded-full bg-white px-8 text-base font-semibold text-violet-700 hover:bg-violet-50">
                {heroCta} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
              <Button onClick={() => scrollTo("contact")} variant="outline" className="h-12 rounded-full border-white/30 bg-white/10 px-8 text-base font-semibold text-white hover:bg-white/20">
                {ctaBtnContact}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8 lg:pb-28">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-violet-600">{contactTitle}</span>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight sm:text-4xl">{contactTitle}</h2>
          <p className="mt-4 text-base text-slate-600">{contactSub}</p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-5 lg:gap-8">
          <div className="space-y-4 lg:col-span-2">
            <div className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100"><MapPin className="h-5 w-5 text-violet-600" /></div>
              <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{contactAddrLabel}</p><p className="mt-1 font-medium text-slate-800">{address}</p></div>
            </div>
            <a href={phoneHref} data-testid="contact-phone" className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all hover:border-violet-200 hover:shadow-[0_8px_30px_rgba(80,40,180,0.08)]">
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100"><Phone className="h-5 w-5 text-blue-600" /></div>
              <div><p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{contactPhoneLabel}</p><p className="mt-1 font-medium text-slate-800">{phone}</p></div>
            </a>
            <div className="rounded-2xl border border-dashed border-violet-200 bg-violet-50/50 p-5 text-center">
              <p className="text-sm font-medium text-violet-800">{contactCallout}</p>
              <a href={phoneHref} className="mt-2 inline-block font-heading text-lg font-bold text-violet-600 hover:underline">{phone}</a>
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-[0_2px_12px_rgba(0,0,0,0.04)] lg:col-span-3" data-testid="map-placeholder">
            <iframe
              title="Girne Harita"
              className="h-80 w-full lg:h-full lg:min-h-[420px]"
              loading="lazy"
              src={`https://www.google.com/maps?q=${encodeURIComponent(cms?.map_query || address)}&output=embed`}
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-16 text-slate-300" data-testid="landing-footer">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            <div>
              <Logo className="h-10 w-auto object-contain" alt="RENKA" />
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">{footerTagline}</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{footerLinksTitle}</p>
              <nav className="mt-4 flex flex-col gap-2">
                {anchorNav.map(([fn, label]) => (
                  <button key={label} onClick={fn} className="text-left text-sm text-slate-400 transition-colors hover:text-white">{label}</button>
                ))}
                {footerPages.map((p) => (
                  <Link key={p.id} to={`/sayfa/${p.slug}`} className="text-left text-sm text-slate-400 transition-colors hover:text-white">{p.title}</Link>
                ))}
              </nav>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{footerDownloadLabel}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                <a href={iosUrl} target={iosUrl === "#" ? undefined : "_blank"} rel="noopener noreferrer" data-testid="appstore-btn" className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 transition-colors hover:bg-white/10">
                  <Apple className="h-6 w-6" /><span className="text-left text-xs leading-tight"><span className="block text-[10px] text-slate-400">Download on the</span><span className="block text-sm font-semibold text-white">App Store</span></span>
                </a>
                <a href={androidUrl} target={androidUrl === "#" ? undefined : "_blank"} rel="noopener noreferrer" data-testid="googleplay-btn" className="flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 transition-colors hover:bg-white/10">
                  <Play className="h-6 w-6" /><span className="text-left text-xs leading-tight"><span className="block text-[10px] text-slate-400">GET IT ON</span><span className="block text-sm font-semibold text-white">Google Play</span></span>
                </a>
              </div>
            </div>
          </div>
          <div className="mt-12 border-t border-white/10 pt-8 text-center">
            <p className="text-xs text-slate-500">© {new Date().getFullYear()} Renka Özel Eğitim ve Gelişim Merkezi. {footerRights}</p>
            <div className="mt-3 flex items-center justify-center gap-2">
              <span className="text-xs text-slate-500">{t.designed}</span>
              <a href="https://gogudijital.com" target="_blank" rel="noopener noreferrer" data-testid="gogudijital-link" className="transition-opacity hover:opacity-100">
                <img src="/gogudijital.webp" alt="Gogu Dijital" className="h-5 w-auto object-contain opacity-90" data-testid="gogudijital-logo" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {showTop && (
        <button onClick={scrollTop} data-testid="scroll-top-btn" aria-label="Yukarı çık"
          className="fixed bottom-6 right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-violet-600 text-white shadow-lg shadow-violet-600/30 transition-all hover:-translate-y-0.5 hover:bg-violet-700">
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
}
